"use professional"
import { useState, useEffect } from "react"
import { useAuth } from "../../AuthContext" // Import useAuth
import { useNavigate } from "react-router-dom"
import { useLanguage } from "../../contexts/LanguageContext";

function WorkExperienceForm({ existingRecords,onSubmitSuccess }) {
  const [numExperiences, setNumExperiences] = useState(existingRecords.length || 1);
  const [experiences, setExperiences] = useState([]);
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const proId = user?.secondId || 0; 

  useEffect(() => {
    if (existingRecords.length > 0) {
      const mappedRecords = existingRecords.map(record => ({
        jobTitle: record.role || "",
        companyName: record.company_name || "",
        startDate: record.start_date || "",
        endDate: record.end_date || "",
        responsibilities: record.responsibilities || "",
        experienceId: record.experience_id || "",
      }));
      setExperiences(mappedRecords);
    } else {
      setExperiences(Array(numExperiences).fill({
        jobTitle: "",
        companyName: "",
        startDate: "",
        endDate: "",
        responsibilities: "",
      }));
    }
  }, [existingRecords, numExperiences]);

  const handleNumExperiencesChange = (e) => {
    const num = Number.parseInt(e.target.value, 10);
    setNumExperiences(num);
    setExperiences(Array(num).fill({}));
  };

  const handleExperienceChange = (index, field, value) => {
    const updatedExperiences = [...experiences];
    updatedExperiences[index] = { ...updatedExperiences[index], [field]: value };
    setExperiences(updatedExperiences);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = existingRecords.length > 0 
        ? "http://localhost:5000/update-work-experience" 
        : `http://localhost:5000/work-experience/${proId}`;
      const method = existingRecords.length > 0 ? "PUT" : "POST";
      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ experiences }),
      });
      if (response.ok) {
        alert(existingRecords.length > 0 ? t('workExperienceUpdatedSuccess') : t('workExperienceSubmittedSuccess'));
        if (onSubmitSuccess) onSubmitSuccess();
        navigate("/ProProject");
      } else {
        alert(t('workExperienceSubmitFailed'));
      }
    } catch (error) {
      console.error("Error submitting work experience:", error);
      alert(t('workExperienceSubmitError'));
    }
  };

  const inputStyle = {
    background: 'var(--input-bg)', color: 'var(--text-primary)', border: '1px solid var(--input-border)',
    borderRadius: '10px', padding: '10px 14px', width: '100%', outline: 'none', fontSize: '0.9rem',
    transition: 'border-color 0.2s, background 0.2s',
  };
  const labelStyle = { display: 'block', color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, marginBottom: '6px' };

  return (
    <div style={{ maxWidth: '720px', margin: '40px auto', background: 'var(--bg-card)', borderRadius: '20px', border: '1px solid var(--border-glass)', padding: '40px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #10b981, var(--accent-violet))' }} />
      <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', marginBottom: '6px' }}>{existingRecords.length > 0 ? t('editWorkExperience') : t('addWorkExperience')}</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: '24px', fontSize: '0.9rem' }}>
        {t('workExperienceSubtitle')}
      </p>
      {!existingRecords.length && (
        <div className="mb-4">
          <label style={labelStyle} htmlFor="numExperiences">{t('numberOfWorkExperiences')}</label>
          <input style={inputStyle} id="numExperiences" type="number" placeholder={t('numberOfWorkExperiencesPlaceholder')} min="1" max="50" required value={numExperiences} onChange={handleNumExperiencesChange}
            onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
        </div>
      )}

      <form onSubmit={handleSubmit} className="grid gap-6">
        {experiences.map((experience, index) => (
          <div key={index} style={{ padding: '20px', border: '1px solid var(--border-glass)', borderRadius: '14px', background: 'var(--bg-glass)', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '16px' }}>{t('workExperience')} {index + 1}</h3>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`jobTitle${index}`}>{t('jobTitle')}</label>
              <input style={inputStyle} id={`jobTitle${index}`} type="text" placeholder={t('jobTitlePlaceholder')} required value={experience.jobTitle || ""}
                onChange={(e) => handleExperienceChange(index, "jobTitle", e.target.value)}
                onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
            </div>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`companyName${index}`}>{t('companyName')}</label>
              <input style={inputStyle} id={`companyName${index}`} type="text" placeholder={t('companyNamePlaceholder')} required value={experience.companyName || ""}
                onChange={(e) => handleExperienceChange(index, "companyName", e.target.value)}
                onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
            </div>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`employmentDuration${index}`}>{t('employmentDuration')}</label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input style={{ ...inputStyle, flex: 1 }} id={`startDate${index}`} type="date" required value={experience.startDate || ""}
                  onChange={(e) => handleExperienceChange(index, "startDate", e.target.value)}
                  onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
                <input style={{ ...inputStyle, flex: 1 }} id={`endDate${index}`} type="date" required value={experience.endDate || ""}
                  onChange={(e) => handleExperienceChange(index, "endDate", e.target.value)}
                  onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
              </div>
            </div>
            <div className="mb-4">
              <label style={labelStyle} htmlFor={`responsibilities${index}`}>{t('keyResponsibilities')}</label>
              <textarea style={{ ...inputStyle, resize: 'vertical' }} id={`responsibilities${index}`} placeholder={t('responsibilitiesPlaceholder')} maxLength="500" rows="4" required value={experience.responsibilities || ""}
                onChange={(e) => handleExperienceChange(index, "responsibilities", e.target.value)}
                onFocus={e => { e.target.style.borderColor = 'rgba(var(--accent-primary-rgb), 0.6)'; e.target.style.background = 'var(--input-focus-bg)'; }} onBlur={e => { e.target.style.borderColor = 'var(--input-border)'; e.target.style.background = 'var(--input-bg)'; }} />
              <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '4px' }}><span>{experience.responsibilities?.length || 0}</span>/500 {t('characters')}</p>
            </div>
          </div>
        ))}

        <div className="flex items-center justify-between">
          <button type="submit"
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none', padding: '12px 32px', borderRadius: '10px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem', transition: 'opacity 0.2s' }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'} onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
            {existingRecords.length > 0 ? t('updateWorkExperience') : t('addWorkExperience')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default WorkExperienceForm;